import type {
  CreateRecipeInput,
  Ingredient,
  MealPlanEntry,
  ParsedRecipe,
  RecipeDetail,
  RecipeListItem,
  RecipeNutrition,
  RecipeNutritionSummary,
  RecipeSuggestion,
  ShoppingListDetail,
  ShoppingListNutrition,
  ShoppingListSummary,
} from "./types";

const rawBase = import.meta.env.VITE_API_BASE ?? "";
const API_BASE = rawBase.replace(/\/$/, "");

interface ApiErrorBody {
  error?: string;
  message?: string;
}

export class ApiError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

function url(path: string) {
  return `${API_BASE}${path}`;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  // Operator API key — only enforced once the Worker's API_KEY secret is
  // set; harmless extra header otherwise. Stored via the header 🔑 button.
  const apiKey = localStorage.getItem("rl_api_key");
  if (apiKey) headers.set("X-Api-Key", apiKey);
  const res = await fetch(url(path), { ...init, headers });
  if (res.status === 401) window.dispatchEvent(new Event("rl-unauthorized"));
  if (!res.ok) {
    let body: ApiErrorBody = {};
    try {
      body = await res.json() as ApiErrorBody;
    } catch {
      body = {};
    }
    throw new ApiError(res.status, body.error ?? "request_failed", body.message ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

export const api = {
  listRecipes(params: { search?: string; favorite?: boolean; meal_slot?: string; tags?: string[] } = {}) {
    const qs = new URLSearchParams();
    if (params.search) qs.set("search", params.search);
    if (params.favorite != null) qs.set("favorite", String(params.favorite));
    if (params.meal_slot) qs.set("meal_slot", params.meal_slot);
    for (const tag of params.tags ?? []) qs.append("tag", tag);
    return request<RecipeListItem[]>(`/api/recipes${qs.size ? `?${qs}` : ""}`);
  },
  getRecipe(id: number) {
    return request<RecipeDetail>(`/api/recipes/${id}`);
  },
  patchRecipe(id: number, body: Partial<CreateRecipeInput>) {
    return request<{ id: number; updated: true; version_bumped: boolean }>(`/api/recipes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  getRecipeNutrition(id: number) {
    return request<RecipeNutrition>(`/api/recipes/${id}/nutrition`);
  },
  getRecipeNutritionSummaries(ids: number[]) {
    const qs = new URLSearchParams();
    if (ids.length) qs.set("ids", ids.join(","));
    return request<RecipeNutritionSummary[]>(`/api/recipes/nutrition-summaries${qs.size ? `?${qs}` : ""}`);
  },
  suggestRecipes(body: { ingredient_ids: number[]; include_pantry_staples?: boolean; cuisine?: string }) {
    return request<RecipeSuggestion[]>("/api/recipes/suggest", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  listRecipeTags(category?: string) {
    const qs = category ? `?category=${encodeURIComponent(category)}` : "";
    return request<{ tag_category: string; tag_value: string }[]>(`/api/recipes/tags${qs}`);
  },
  parseRecipe(text: string) {
    return request<ParsedRecipe>("/api/recipes/parse", {
      method: "POST",
      body: JSON.stringify({ text }),
    });
  },
  importRecipeUrl(url: string) {
    return request<ParsedRecipe>("/api/recipes/import-url", {
      method: "POST",
      body: JSON.stringify({ url }),
    });
  },
  importRecipePhoto(body: { image_base64: string; media_type: string; filename?: string | null }) {
    return request<ParsedRecipe>("/api/recipes/import-photo", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  createRecipe(body: CreateRecipeInput) {
    return request<{ id: number }>("/api/recipes", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  deleteRecipe(id: number) {
    return request<{ id: number; deleted: true }>(`/api/recipes/${id}`, {
      method: "DELETE",
    });
  },
  listIngredients(params: { search?: string; confidence?: string; limit?: number; sort?: "review" } = {}) {
    const qs = new URLSearchParams();
    if (params.search) qs.set("search", params.search);
    if (params.confidence) qs.set("confidence", params.confidence);
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.sort) qs.set("sort", params.sort);
    return request<Ingredient[]>(`/api/ingredients${qs.size ? `?${qs}` : ""}`);
  },
  createIngredient(body: {
    canonical_name: string;
    confidence?: string;
    review_reason?: string | null;
    aliases?: string[];
  }) {
    return request<{ id: number }>("/api/ingredients", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  getIngredient(id: number) {
    return request<Ingredient>(`/api/ingredients/${id}`);
  },
  patchIngredient(id: number, body: Omit<Partial<Ingredient>, "pantry_staple"> & { pantry_staple?: boolean }) {
    return request<{ id: number; updated: true }>(`/api/ingredients/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  mergeIngredient(sourceId: number, targetId: number) {
    return request<{ merged: true; source_id: number; target_id: number; target_name: string }>(
      `/api/ingredients/${sourceId}/merge`,
      { method: "POST", body: JSON.stringify({ target_id: targetId }) },
    );
  },
  deleteIngredient(id: number) {
    return request<{ id: number; deleted: true }>(`/api/ingredients/${id}`, {
      method: "DELETE",
    });
  },
  addIngredientAlias(id: number, alias: string) {
    return request<{ ingredient_id: number; alias: string }>(`/api/ingredients/${id}/aliases`, {
      method: "POST",
      body: JSON.stringify({ alias }),
    });
  },
  addHouseholdUnit(id: number, unit: { unit_name: string; grams_per_unit: number; notes?: string | null }) {
    return request<{ ingredient_id: number; unit_name: string }>(`/api/ingredients/${id}/units`, {
      method: "POST",
      body: JSON.stringify(unit),
    });
  },
  fetchIngredient(id: number) {
    return request<{
      outcome: { status: string; provider?: string; matched_name?: string };
      ingredient: Ingredient;
    }>(`/api/ingredients/${id}/fetch`, {
      method: "POST",
    });
  },
  listShoppingLists() {
    return request<ShoppingListSummary[]>("/api/shopping-lists");
  },
  createShoppingList(body: { name?: string | null; recipes: { recipe_id: number; servings_multiplier?: number }[] }) {
    return request<{ id: number }>("/api/shopping-lists", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  getShoppingList(id: number) {
    return request<ShoppingListDetail>(`/api/shopping-lists/${id}`);
  },
  checkShoppingItem(listId: number, itemId: number, checked: boolean) {
    return request<{ item_id: number; checked: boolean }>(`/api/shopping-lists/${listId}/items/${itemId}`, {
      method: "PATCH",
      body: JSON.stringify({ checked }),
    });
  },
  getShoppingNutrition(id: number) {
    return request<ShoppingListNutrition>(`/api/shopping-lists/${id}/nutrition`);
  },
  getMealPlan() {
    return request<{ entries: MealPlanEntry[] }>("/api/meal-plan");
  },
  addMealPlanEntry(body: { day: number; meal_slot?: string; recipe_id: number; servings_multiplier?: number }) {
    return request<{ id: number }>("/api/meal-plan/entries", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  deleteMealPlanEntry(id: number) {
    return request<{ deleted: number }>(`/api/meal-plan/entries/${id}`, { method: "DELETE" });
  },
  clearMealPlan() {
    return request<{ deleted: number }>("/api/meal-plan", { method: "DELETE" });
  },
  mealPlanToShoppingList(name?: string | null) {
    return request<{ id: number }>("/api/meal-plan/shopping-list", {
      method: "POST",
      body: JSON.stringify({ name: name ?? null }),
    });
  },
  learnNonIngredient(text: string) {
    return request<{ learned: string }>("/api/learning/non-ingredients", {
      method: "POST",
      body: JSON.stringify({ text }),
    });
  },
};
